import React, {useContext, useEffect, useMemo, useState} from 'react';
import {useLocation} from 'react-router-dom';
import {flushSync} from 'react-dom';
import FloatingYoutubePlayer from './FloatingYoutubePlayer';
import SongComponent from './SongComponent';
import DeferredSongBody from './DeferredSongBody';
import {useWorkspaceReadOnly} from '../../../shared/hooks/useWorkspaceReadOnly';
import './SongsPageComponent.css';
import {AppCtx} from "../../../AppCtx";
import AddSongComponent from './AddSongComponent';
import {useSongContentService} from "../hooks/useSongContentService";
import {useEventListener} from '../../../infrastructure/events/useBroadcastChannel';
import {
  AddSongRequestedEvent,
  DeleteSongRequestedEvent,
  EditSongPositionRequestedEvent
} from '../../../infrastructure/events/channels';

const SongsPageComponent = ({children}) => {
  const location = useLocation();
  const [renderAll, setRenderAll] = useState(false);
  const [youtubeVideo, setYoutubeVideo] = useState(null);
  const isReadOnly = useWorkspaceReadOnly();
  const {
    selectedFile,
    songViewFontSize,
    isFileBrowserVisible,
    collapsedSongs,
    setCollapsedSongs,
    isPresentationControlPanelVisible,
    sharedPresentationState,
    sharedGroupSelection,
    isCommonSessionEnabled
  } = useContext(AppCtx);

  const {songsJson} = useSongContentService();

  useEffect(() => {
    setRenderAll(false);
  }, [selectedFile?.name]);
  useEffect(() => {
    const materialize = () => flushSync(() => setRenderAll(true));
    const find = event => {
      if (event.key === 'Escape') setRenderAll(false);
      if (event.key === 'F3' || ((event.ctrlKey || event.metaKey) && ['f', 'g'].includes(event.key.toLowerCase()) && !event.altKey)) materialize();
    };
    window.addEventListener('beforeprint', materialize);
    const release = () => setRenderAll(false);
    window.addEventListener('afterprint', release);
    document.addEventListener('keydown', find);
    return () => {
      window.removeEventListener('beforeprint', materialize);
      window.removeEventListener('afterprint', release);
      document.removeEventListener('keydown', find);
    };
  }, []);

  // Helper to get collapsed songs for current file
  const getCurrentFileCollapsedSongs = useMemo(() => {
    if (!selectedFile?.name) return new Set();
    return collapsedSongs.get(selectedFile.name) || new Set();
  }, [collapsedSongs, selectedFile?.name]);

  // Sprawdź czy wszystkie piosenki są zwinięte
  const areAllSongsCollapsed = useMemo(() => {
    if (!songsJson || !songsJson.songs) return false;

    // Jeśli brak piosenek, uznajemy warunek za spełniony
    if (songsJson.songs.length === 0) return true;

    // Wszystkie piosenki są zwinięte, jeśli liczba zwiniętych piosenek
    // jest równa liczbie wszystkich piosenek
    return getCurrentFileCollapsedSongs.size === songsJson.songs.length;
  }, [getCurrentFileCollapsedSongs, songsJson]);

  // Nasłuchuj zdarzenia dodania nowej piosenki, aby automatycznie ją zwinąć
  useEventListener(AddSongRequestedEvent, () => {
    if (songsJson && songsJson.songs.length > 0 && selectedFile?.name) {
      // Po dodaniu piosenki, nowa piosenka będzie miała indeks równy długości tablicy
      const newSongIndex = songsJson.songs.length;
      // Dodaj nowy indeks do zbioru zwiniętych piosenek dla tego pliku
      setCollapsedSongs(prev => {
        const newMap = new Map(prev);
        const fileCollapsed = newMap.get(selectedFile.name) || new Set();
        const newSet = new Set(fileCollapsed);
        newSet.add(newSongIndex);
        newMap.set(selectedFile.name, newSet);
        return newMap;
      });
    }
  });

  // Nasłuchuj zdarzenia usunięcia piosenki i dostosuj indeksy
  useEventListener(DeleteSongRequestedEvent, (message) => {
    if (selectedFile?.name === message.fileName) {
      const deletedIndex = message.songIndex;
      
      setCollapsedSongs(prev => {
        const newMap = new Map(prev);
        const fileCollapsed = newMap.get(selectedFile.name) || new Set();
        const newSet = new Set();
        
        // Iterate through all collapsed indices for this file
        fileCollapsed.forEach(index => {
          if (index < deletedIndex) {
            // Indices before deleted song remain the same
            newSet.add(index);
          } else if (index > deletedIndex) {
            // Indices after deleted song are decremented by 1
            newSet.add(index - 1);
          }
          // index === deletedIndex is removed (not added to newSet)
        });
        
        newMap.set(selectedFile.name, newSet);
        return newMap;
      });
    }
  });

  // Nasłuchuj zdarzenia zmiany pozycji piosenki i dostosuj indeksy
  useEventListener(EditSongPositionRequestedEvent, (message) => {
    if (selectedFile?.name === message.targetFileName) {
      const oldPos = message.oldPosition;
      const newPos = message.newPosition;
      
      setCollapsedSongs(prev => {
        const newMap = new Map(prev);
        const fileCollapsed = newMap.get(selectedFile.name) || new Set();
        const newSet = new Set();
        
        // Convert positions to indices (position is 1-based, index is 0-based)
        const oldIndex = oldPos - 1;
        const newIndex = newPos - 1;
        
        // Check if the moved song was collapsed
        const movedSongWasCollapsed = fileCollapsed.has(oldIndex);
        
        fileCollapsed.forEach(index => {
          if (index === oldIndex) {
            // The moved song gets the new index
            if (movedSongWasCollapsed) {
              newSet.add(newIndex);
            }
          } else if (oldIndex < newIndex) {
            // Moving down: indices between old and new positions shift up
            if (index > oldIndex && index <= newIndex) {
              newSet.add(index - 1);
            } else {
              newSet.add(index);
            }
          } else if (oldIndex > newIndex) {
            // Moving up: indices between new and old positions shift down
            if (index >= newIndex && index < oldIndex) {
              newSet.add(index + 1);
            } else {
              newSet.add(index);
            }
          } else {
            // Same position, no change
            newSet.add(index);
          }
        });
        
        newMap.set(selectedFile.name, newSet);
        return newMap;
      });
    }
  });

  // Auto-expand current song when navigating via remote control panel
  useEffect(() => {
    if (!isPresentationControlPanelVisible || !sharedPresentationState || !songsJson || !selectedFile?.name) {
      return;
    }

    // Check if the shared state is for the current file
    if (sharedPresentationState.fileName !== selectedFile.name) {
      return;
    }

    const songNumber = sharedPresentationState.songNumber;
    if (typeof songNumber !== 'number' || songNumber < 1) {
      return;
    }

    // Convert song number (1-based) to index (0-based)
    const songIndex = songNumber - 1;

    // Check if this song index is valid
    if (songIndex < 0 || songIndex >= songsJson.songs.length) {
      return;
    }

    // Expand the current song and collapse all others
    setCollapsedSongs(prev => {
      const newMap = new Map(prev);
      const newSet = new Set();

      // Collapse all songs except the current one
      for (let i = 0; i < songsJson.songs.length; i++) {
        if (i !== songIndex) {
          newSet.add(i);
        }
      }

      newMap.set(selectedFile.name, newSet);
      return newMap;
    });
  }, [isPresentationControlPanelVisible, sharedPresentationState, songsJson, selectedFile?.name, setCollapsedSongs]);

  useEffect(() => {
    if (!isCommonSessionEnabled || sharedGroupSelection?.fileName !== selectedFile?.name) return;
    const index = Number(sharedGroupSelection?.songNumber) - 1;
    if (!Number.isSafeInteger(index) || index < 0 || index >= (songsJson?.songs?.length || 0)) return;
    setCollapsedSongs(previous => {
      const collapsed = previous.get(selectedFile.name);
      if (!collapsed?.has(index)) return previous;
      const next = new Map(previous), expanded = new Set(collapsed);
      expanded.delete(index);
      next.set(selectedFile.name, expanded);
      return next;
    });
  }, [isCommonSessionEnabled, sharedGroupSelection, selectedFile?.name, songsJson, setCollapsedSongs]);

  return (
    <>
      {isFileBrowserVisible && (
        <div>
          {children}
        </div>
      )}
      <div id="songs" style={{fontSize: `${songViewFontSize}em`}}>
        {songsJson && songsJson.songs.map((song, index) => (
          <DeferredSongBody key={`${selectedFile?.name}:${index}`} anchorNumber={index + 1} layoutKey={songViewFontSize}
            defer={songsJson.songs.length > 20 && index > 0 && !renderAll && !areAllSongsCollapsed &&
              Number(selectedFile?.songNumber) !== index + 1 && location.hash !== `#song-title-${index + 1}` &&
              !(isCommonSessionEnabled && sharedGroupSelection?.fileName === selectedFile?.name && Number(sharedGroupSelection?.songNumber) === index + 1) &&
              !(sharedPresentationState?.fileName === selectedFile?.name && sharedPresentationState?.songNumber === index + 1)}
            estimatedHeight={getCurrentFileCollapsedSongs.has(index) ? 3 : Math.max(12, (song.lines?.length || 10) * 1.4)}>
            <SongComponent
              deferBody={false}
              index={index}
              song={song}
              selectedFile={selectedFile}
              areAllSongsCollapsed={areAllSongsCollapsed}
              totalSongs={songsJson.songs.length}
              onYoutubeOpen={setYoutubeVideo}
            />
          </DeferredSongBody>
        ))}

        {/* Komponent dodawania piosenek widoczny tylko gdy wszystkie piosenki są zwinięte */}
        {!isReadOnly && selectedFile && areAllSongsCollapsed && <AddSongComponent selectedFile={selectedFile}/>}
      </div>
      {youtubeVideo && <FloatingYoutubePlayer video={youtubeVideo} onClose={() => setYoutubeVideo(null)}/>}
    </>
  );
};

export default SongsPageComponent;
